import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { g, p, del, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePlan } from "@/context/PlanContext";
import { useI18n } from "@/i18n";
import { PremiumLock } from "@/components/PremiumLock";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hourglass, UserPlus, MessageCircle, Check, X, Clock } from "lucide-react";

const badge = (m) =>
  m === null ? "bg-slate-100 text-slate-600"
    : m === 0 ? "bg-emerald-100 text-emerald-700"
      : m <= 20 ? "bg-amber-100 text-amber-800"
        : "bg-rose-100 text-rose-700";

export default function Attesa() {
  const { user, settings } = useApp();
  const { has } = usePlan();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ name: "", people: 2, phone: "", note: "" });

  const load = useCallback(async () => {
    if (!has("waitlist")) return;
    try {
      setData(await g("/waitlist"));
    } catch (e) {
      /* piano non abilitato */
    }
  }, [has]);
  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, [load]);

  if (!has("waitlist"))
    return (
      <PremiumLock
        title={t("w.title")}
        description={t("w.sub")}
        needed="standard"
        message={t("w.title")}
        preview={
          <div className="space-y-3 p-6">
            {[["Famiglia Rossi", 4, 0], ["Bianchi", 2, 15]].map(([n, pe, m]) => (
              <Card key={n} className="flex items-center justify-between p-4">
                <span className="text-sm font-semibold">{n} · {pe} pers.</span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${badge(m)}`}>{m === 0 ? t("w.now") : `~${m}′`}</span>
              </Card>
            ))}
          </div>
        }
      />
    );

  const add = async () => {
    if (!f.name.trim()) return toast.error(t("w.needName"));
    try {
      setData(await p("/waitlist", { ...f, people: Number(f.people) || 1 }));
      setF({ name: "", people: 2, phone: "", note: "" });
      toast.success(t("w.added"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const act = async (r, kind) => {
    try {
      if (kind === "notify") {
        setData(await p(`/waitlist/${r.id}/notify`, { by: user.name }));
        if (r.phone) {
          const sign = settings.motto && settings.motto_on_messages !== false ? `\n— ${settings.business_name}: «${settings.motto}»` : `\n— ${settings.business_name}`;
          window.open(`sms:${r.phone}?&body=${encodeURIComponent(t("w.smsBody", { name: r.name }) + sign)}`, "_self");
        }
        toast.success(t("w.notified", { name: r.name }));
      } else if (kind === "seat") {
        setData(await p(`/waitlist/${r.id}/seat`, { by: user.name, table: r.table_hint || "" }));
        toast.success(t("w.seated", { name: r.name }));
      } else {
        setData(await del(`/waitlist/${r.id}`));
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const rows = data?.rows || [];

  return (
    <div className="fade-up space-y-5" data-testid="attesa-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("w.overline")}</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("w.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">
          {t("w.avgTurn", { n: data?.avg_turn ?? 0 })} · {t("w.freeNow", { n: data?.free_now ?? 0 })}
        </p>
      </div>

      <Card className="p-5" data-testid="waitlist-form">
        <h3 className="flex items-center gap-2 text-lg font-semibold">
          <UserPlus className="h-4 w-4" /> {t("w.add")}
        </h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Label className="text-xs">{t("w.name")}</Label>
            <Input className="mt-1 h-11" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="wait-name-input" />
          </div>
          <div>
            <Label className="text-xs">{t("w.people")}</Label>
            <Input className="mt-1 h-11" type="number" min="1" value={f.people} onChange={(e) => setF({ ...f, people: e.target.value })} data-testid="wait-people-input" />
          </div>
          <div>
            <Label className="text-xs">{t("w.phone")}</Label>
            <Input className="mt-1 h-11" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="wait-phone-input" />
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label className="text-xs">{t("w.note")}</Label>
            <Input className="mt-1 h-11" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder={t("w.notePh")} data-testid="wait-note-input" />
          </div>
          <Button className="h-11" onClick={add} data-testid="wait-add-btn">
            {t("w.addBtn")}
          </Button>
        </div>
      </Card>

      <Card className="p-5" data-testid="waitlist-queue">
        <h3 className="flex items-center gap-2 text-lg font-semibold">
          <Hourglass className="h-4 w-4" /> {t("w.queue", { n: rows.length })}
        </h3>
        {rows.length === 0 && <p className="mt-2 text-sm text-slate-500">{t("w.empty")}</p>}
        <div className="mt-3 space-y-2">
          {rows.map((r, i) => (
            <div key={r.id} className="rounded-md border border-slate-200 p-3" data-testid={`wait-row-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-semibold">
                    <span className="font-num mr-1.5 text-slate-400">{i + 1}.</span>
                    {r.name} <span className="text-xs font-normal text-slate-500">· {r.people} {t("w.pers")}</span>
                  </p>
                  <p className="text-[11px] text-slate-500">
                    <Clock className="mr-0.5 inline h-3 w-3" />
                    {t("w.since", { n: r.waiting_minutes })}
                    {r.table_hint && ` · ${t("w.table", { table: r.table_hint })}`}
                    {r.note && ` · ${r.note}`}
                    {r.status === "avvisato" && ` · ${t("w.alreadyNotified")}`}
                  </p>
                </div>
                <span className={`font-num rounded-full px-2.5 py-1 text-xs font-bold ${badge(r.quoted_minutes)}`} data-testid={`wait-quote-${r.id}`}>
                  {r.quoted_minutes === null ? t("w.noTable") : r.quoted_minutes === 0 ? t("w.now") : `~${r.quoted_minutes}′`}
                </span>
              </div>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <Button size="sm" variant={r.status === "avvisato" ? "outline" : "default"} onClick={() => act(r, "notify")} data-testid={`wait-notify-${r.id}`}>
                  <MessageCircle className="mr-1 h-3.5 w-3.5" /> {t("w.notify")}
                </Button>
                {r.phone && (
                  <>
                    <Button size="sm" variant="outline" data-testid={`wait-wa-${r.id}`}
                            onClick={async () => {
                              const a = await g(`/waitlist/${r.id}/avviso`, { table: r.table_hint || "" });
                              window.open(a.whatsapp, "_blank");
                              act(r, "notify");
                            }}>
                      WhatsApp
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`wait-sms-${r.id}`}
                            onClick={async () => {
                              const a = await g(`/waitlist/${r.id}/avviso`, { table: r.table_hint || "" });
                              window.location.href = a.sms;
                              act(r, "notify");
                            }}>
                      SMS
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={() => act(r, "seat")} data-testid={`wait-seat-${r.id}`}>
                  <Check className="mr-1 h-3.5 w-3.5" /> {t("w.seat")}
                </Button>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => act(r, "drop")} data-testid={`wait-drop-${r.id}`}>
                  <X className="mr-1 h-3.5 w-3.5" /> {t("w.gone")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {(data?.done || []).length > 0 && (
        <Card className="p-5" data-testid="waitlist-done">
          <h3 className="text-lg font-semibold">{t("w.done")}</h3>
          <div className="mt-3 space-y-1 text-xs text-slate-500">
            {data.done.map((r) => (
              <p key={r.id} data-testid={`wait-done-${r.id}`}>
                {r.name} · {r.people} {t("w.pers")} · {r.status === "seduto" ? t("w.seatedAt", { table: r.table || "—" }) : t("w.gone")} ·{" "}
                {new Date(r.seated_at || r.at).toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" })}
              </p>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
